import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { size as length, map } from "lodash";
import { UnControlled as CodeMirror } from "react-codemirror2";
import styled, { css } from "styled-components";
import "codemirror/lib/codemirror.css";
import {
  FontSize,
  Spacing,
  content,
  dominant,
  form,
  message,
  transparentize,
} from "@glossy";
import { prettyScrollbars } from "@glossy/mixins";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import { useOnClickOutside } from "@lkf/hooks";
import Button from "@ui-kit/button";
import { SIZES, Size } from "@ui-kit/form/styles";
import { MODE } from "./codemirror";
import { tokenHighlights } from "./token";

interface IProps {
  value?: string;
  placeholder?: string;
  size?: Size;
  variant?: Variant;
  locale: Locale;
  options?: any;
  syntaxError?: boolean;
  otherErrors?: string[];
  firstMount?: boolean;
  visible?: boolean;
  showCounter?: boolean;
  limitExceeded?: boolean;
  maxHeight?: number;
  isDisabled?: boolean;
  onClickExpand?: () => void;
  onChange?: (newValue: string) => void;
  onFocus?: () => void;
  onEnter?: (newValue: string) => void;
  onClickOutside?: (event: PossibleEvent) => void;
  className?: string;
}

type PossibleEvent = MouseEvent | TouchEvent;

const VARIANTS = {
  primary: css`
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  `,
  secondary: css`
    background-color: transparent;
    box-shadow: 0 0 0 1px
      ${(props) => transparentize(0.5, form(props, "primary"))} inset;
  `,
  tertiary: css`
    border-radius: 0;
    background-color: transparent;
    box-shadow: 0 -1px 0 0 ${(props) =>
        transparentize(0.5, form(props, "primary"))} inset;
  `,
};
export type Variant = keyof typeof VARIANTS;

const HOVER_VARIANTS = {
  primary: css`
    background-color: ${(props) => transparentize(0.8, form(props, "primary"))};
  `,
  secondary: css`
    box-shadow: 0 0 0 1px ${form} inset;
  `,
  tertiary: css`
    box-shadow: 0 -1px 0 0 ${form} inset;
  `,
};

const FOCUS_VARIANTS = {
  primary: css`
    box-shadow: 0 0 0 2px ${dominant} inset;
    background-color: transparent;
  `,
  secondary: css`
    box-shadow: 0 0 0 2px ${dominant} inset;
  `,
  tertiary: css`
    box-shadow: 0 -2px 0 0 ${dominant} inset;
  `,
};

const LINE_HEIGHT = 18;

const Container = styled.div<{
  error: boolean;
  $size: Size;
  variant: Variant;
  firstMount: boolean;
  expandable: boolean;
  maxHeight?: number;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.ExtraSmall};
  ${({ firstMount, variant }) => firstMount && FOCUS_VARIANTS[variant]};
  border-radius: ${(props) => SIZES[props.$size].radius}px;
  ${tokenHighlights};

  .CodeMirror {
    padding: ${({ $size }) => (SIZES[$size].height - LINE_HEIGHT) / 2}px 12px;
    padding-bottom: ${({ expandable }) => expandable && "36px"};
    height: auto;
    overflow: hidden;
    border-radius: ${(props) => SIZES[props.$size].radius}px;
    ${(props) => VARIANTS[props.variant]};
    box-shadow: ${(props) =>
      props.error &&
      (props.variant === "tertiary"
        ? `0 -2px 0 0 ${message(props, "error")} inset !important`
        : `0 0 0 2px ${message(props, "error")} inset !important`)};
  }

  .CodeMirror-cursor {
    border-left: solid 1px ${form};
  }

  .CodeMirror-scroll {
    height: ${(props) => (props.maxHeight ? `${props.maxHeight}px` : "auto")};
    overflow-y: ${(props) =>
      props.maxHeight ? "scroll" : "hidden "}!important;
    overflow-x: auto !important;

    ${(props) =>
      props.expandable &&
      `margin-right: -8px;
      margin-bottom: -4px;
    `};
    ${(props) => props.maxHeight && prettyScrollbars()}
  }

  .CodeMirror-sizer {
    min-height: ${LINE_HEIGHT}px !important; /* Avoid flattening when text is empty */
  }

  .CodeMirror-lines {
    padding: 0;
  }

  .CodeMirror:not(.CodeMirror-focused):hover {
    ${(props) => HOVER_VARIANTS[props.variant]};
  }

  .CodeMirror-focused {
    ${(props) => FOCUS_VARIANTS[props.variant]};
  }
`;

const CodeMirrorWrapper = styled.div`
  position: relative;
`;

const StyledCodeMirror = styled(CodeMirror)<{
  $size: Size; // "$size" to avoid conflict with HTML input "size" property
  $variant: Variant;
  isDisabled?: boolean;
}>`
  pointer-events: ${({ isDisabled }) => isDisabled && "none"};
  line-height: ${LINE_HEIGHT}px;
  font-size: ${FontSize.Medium};
  text-overflow: ellipsis;
  width: 100%;
  box-sizing: border-box;
`;

const Placeholder = styled.div<{ $size: Size }>`
  position: absolute;
  font-size: ${FontSize.Medium};
  color: ${form};
  opacity: 0.7;
  top: ${({ $size }) => SIZES[$size].height / 2}px;
  transform: translateY(-50%);
  left: 16px;
  overflow: hidden;
  pointer-events: none;
`;

const Counter = styled.div<{ limitExceeded?: boolean }>`
  font-size: ${FontSize.Small};
  color: ${(props) =>
    props.limitExceeded
      ? message(props, "error")
      : content(props, "secondary")};
  text-align: right;
`;

const Error = styled.div`
  color: ${(props) => message(props, "error")};
  font-size: ${FontSize.Small};
  text-align: right;
`;

const ExpandButton = styled(Button)`
  position: absolute;
  bottom: 6px;
  right: 6px;
`;

const OPTIONS = {
  viewportMargin: Infinity,
  lineWrapping: true,
  scrollbarStyle: null,
  mode: "linkfluence-query",
};

const UiKitFormSearch = forwardRef(
  (
    {
      value,
      placeholder,
      size = "m",
      variant = "secondary",
      locale,
      firstMount = false,
      options = {},
      syntaxError = false,
      otherErrors,
      visible,
      showCounter,
      limitExceeded,
      maxHeight,
      onFocus,
      onEnter,
      onChange,
      onClickOutside,
      onClickExpand,
      className,
      isDisabled,
    }: IProps,
    ref
  ) => {
    const codeMirrorRef = useRef(null);
    const refContainer = useRef<HTMLDivElement>(null);

    const hasErrors = syntaxError || length(otherErrors) > 0;
    const characterCount = length(value);
    const showPlaceholder = placeholder && !value;

    // Allow parent to trigger actions on codemirror
    useImperativeHandle(ref, () => ({
      onSelection(selection: string) {
        const codemirror = codeMirrorRef.current.editor;
        const value: string = codemirror.getValue();

        if (value !== value.trim()) {
          codemirror.setValue(value.trim());
          codemirror.setCursor(codemirror.lineCount(), 0); // Move cursor to the end
        }

        codemirror.replaceSelection(" " + selection);
        codemirror.focus();
      },
    }));

    useEffect(() => {
      const codemirror = codeMirrorRef.current.editor;
      codemirror.setOption("extraKeys", {
        Enter: (cm) => {
          if (onEnter) {
            onEnter(cm.getValue());
            cm.getInputField().blur();
          }
          return cm.Pass;
        },
      });
    }, [onEnter]);

    useEffect(() => {
      const codemirror = codeMirrorRef.current.editor;
      const currentValue = codemirror.getValue();
      if (currentValue !== value) {
        codemirror.setValue(value);
      }
    }, [value]);

    // Refresh when opening to force CodeMirror to adapt to its content
    useEffect(() => {
      if (visible) {
        const codemirror = codeMirrorRef.current.editor;
        codemirror.refresh();

        // Focus end of input
        if (!isDisabled) {
          codemirror.focus();
        }
        codemirror.setCursor(codemirror.lineCount(), 0);
      }
    }, [isDisabled, visible]);

    useOnClickOutside(refContainer, onClickOutside);

    return (
      <Container
        error={hasErrors}
        ref={refContainer}
        $size={size}
        variant={variant}
        firstMount={firstMount}
        maxHeight={maxHeight}
        className={className}
        expandable={!!onClickExpand}
      >
        <CodeMirrorWrapper>
          <StyledCodeMirror
            ref={codeMirrorRef}
            defineMode={MODE}
            options={{ ...OPTIONS, ...options }}
            value={value}
            onFocus={() => {
              onFocus?.();
            }}
            onChange={(editor, data, value) => {
              onChange?.(value);
            }}
            $size={size}
            $variant={variant}
            autoCursor={false}
            isDisabled={isDisabled}
          />
          {onClickExpand && (
            <ExpandButton
              icon="arrows-spread"
              variant="tertiary"
              size="xs"
              onClick={onClickExpand}
            />
          )}
        </CodeMirrorWrapper>
        {showPlaceholder && (
          <Placeholder $size={size}>{placeholder}</Placeholder>
        )}
        {showCounter && (
          <Counter limitExceeded={limitExceeded}>
            {tr(locale, "UI-KIT::FORM::CHARACTERS", { count: characterCount })}
          </Counter>
        )}
        {hasErrors && (
          <>
            {map(otherErrors, (error, id) => (
              <Error key={id}>{error}</Error>
            ))}
            {syntaxError && (
              <Error>{tr(locale, "UI-KIT::FORM::SYNTAX_ERROR")}</Error>
            )}
          </>
        )}
      </Container>
    );
  }
);

export default UiKitFormSearch;
